import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import * as priceChangeBasisApi from '../../api/priceChangeBasisApi';
import { useSnackbar } from '../../context/SnackbarContext';
import { useAuth } from '../../context/AuthContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const EMPTY_FORM = { name: '', description: '', isActive: true };

export default function PriceChangeBasisTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { showSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('MANAGE_MASTER_DATA');
  const canDelete = hasPermission('DELETE_MASTER_DATA') || hasPermission('MANAGE_MASTER_DATA');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await priceChangeBasisApi.getAll();
      setItems(res.data || res);
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setLoading(false); }
  }, [showSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditItem(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (item) => { setEditItem(item); setForm({ name: item.name, description: item.description || '', isActive: item.isActive }); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editItem) { await priceChangeBasisApi.update(editItem.id, form); showSnackbar('Basis updated', 'success'); }
      else { await priceChangeBasisApi.create(form); showSnackbar('Basis created', 'success'); }
      setDialogOpen(false); load();
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await priceChangeBasisApi.remove(deleteTarget.id); showSnackbar('Basis deleted', 'success'); setDeleteTarget(null); load(); }
    catch (e) { showSnackbar(e.message, 'error'); setDeleteTarget(null); }
  };

  if (!canManage && !canDelete) return <Alert severity="info">You have read-only access to price change basis options.</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} size="small">Add Basis</Button>
        )}
      </Box>

      {loading ? <LoadingSpinner /> : (
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell><TableCell>Name</TableCell><TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              {(canManage || canDelete) && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, i) => (
              <TableRow key={item.id} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell fontWeight={600}>{item.name}</TableCell>
                <TableCell sx={{ color: '#757575' }}>{item.description || '—'}</TableCell>
                <TableCell><Chip label={item.isActive ? 'Active' : 'Inactive'} size="small" color={item.isActive ? 'success' : 'default'} /></TableCell>
                {(canManage || canDelete) && (
                  <TableCell align="right">
                    {canManage && (
                      <IconButton size="small" onClick={() => openEdit(item)}><EditIcon fontSize="small" /></IconButton>
                    )}
                    {canDelete && (
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(item)}><DeleteIcon fontSize="small" /></IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!items.length && <TableRow><TableCell colSpan={canManage || canDelete ? 5 : 4} align="center" sx={{ py: 4, color: '#9e9e9e' }}>No basis options defined</TableCell></TableRow>}
          </TableBody>
        </Table>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editItem ? 'Edit Basis' : 'Add Price Change Basis'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Name" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField label="Description" fullWidth multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional description..." />
          {editItem && <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />} label="Active" />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name || saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={!!deleteTarget} title="Delete Basis" message={`Delete "${deleteTarget?.name}"? Existing price history entries will retain the reference.`} onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
    </Box>
  );
}
